'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Pencil,
  Trash2,
  Check,
  Plus,
  X,
  Copy,
  GripVertical,
  Type,
  AlignLeft,
  Hash,
  Calendar,
  List,
  CheckSquare,
  Phone,
  AtSign
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import type { LocalField, DynamicFieldType } from '../types';

interface FieldEditorCardProps {
  field: LocalField;
  existingLabels: string[];
  onUpdate: (tempId: string, updates: Partial<LocalField>) => void;
  onRemove: (tempId: string) => void;
  onDuplicate?: (tempId: string) => void;
}

const FIELD_TYPE_ICONS: Record<
  DynamicFieldType,
  React.ComponentType<{ className?: string }>
> = {
  short_text: Type,
  long_text: AlignLeft,
  number: Hash,
  date: Calendar,
  selection_list: List,
  yes_no: CheckSquare,
  phone: Phone,
  email: AtSign
};

const FIELD_TYPE_LABEL_KEYS: Record<DynamicFieldType, string> = {
  short_text: 'fieldShortText',
  long_text: 'fieldLongText',
  number: 'fieldNumber',
  date: 'fieldDate',
  selection_list: 'fieldSelectionList',
  yes_no: 'fieldYesNo',
  phone: 'fieldPhone',
  email: 'fieldEmail'
};

export function FieldEditorCard({
  field,
  existingLabels,
  onUpdate,
  onRemove,
  onDuplicate
}: FieldEditorCardProps) {
  const t = useTranslations('dynamicForms');
  const [isEditing, setIsEditing] = useState(!field.label);
  const [editLabel, setEditLabel] = useState(field.label);
  const [editRequired, setEditRequired] = useState(field.is_required);
  const [editOptions, setEditOptions] = useState<string[]>(
    field.options.length > 0 ? field.options : ['', '']
  );
  const [editDefaultValue, setEditDefaultValue] = useState(
    field.default_value || ''
  );
  const [editMultiSelect, setEditMultiSelect] = useState(
    field.is_multi_select ?? false
  );

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: field.tempId, disabled: isEditing });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };

  const Icon = FIELD_TYPE_ICONS[field.type];

  const [labelError, setLabelError] = useState('');

  function handleDone() {
    const trimmedLabel = editLabel.trim();
    if (!trimmedLabel) return;

    if (
      existingLabels.some((l) => l.toLowerCase() === trimmedLabel.toLowerCase())
    ) {
      setLabelError(t('duplicateFieldLabel'));
      return;
    }
    setLabelError('');

    const updates: Partial<LocalField> = {
      label: trimmedLabel,
      is_required: editRequired
    };

    if (field.type === 'selection_list') {
      const filteredOptions = editOptions.filter((o) => o.trim());
      updates.options = filteredOptions;
      updates.is_multi_select = editMultiSelect;
      updates.default_value = filteredOptions.includes(editDefaultValue)
        ? editDefaultValue
        : '';
    } else if (field.type === 'number') {
      updates.default_value = editDefaultValue.trim();
    }

    onUpdate(field.tempId, updates);
    setIsEditing(false);
  }

  function handleAddOption() {
    setEditOptions([...editOptions, '']);
  }

  function handleRemoveOption(index: number) {
    if (editOptions[index] === editDefaultValue) {
      setEditDefaultValue('');
    }
    setEditOptions(editOptions.filter((_, i) => i !== index));
  }

  function handleOptionChange(index: number, value: string) {
    if (editOptions[index] === editDefaultValue) {
      setEditDefaultValue(value);
    }
    const newOptions = [...editOptions];
    newOptions[index] = value;
    setEditOptions(newOptions);
  }

  if (isEditing) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className='border-primary/50 rounded-lg border bg-green-50/30 p-4 dark:bg-green-950/10'
      >
        <div className='space-y-4'>
          {/* Type badge header + delete */}
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-2'>
              <Icon className='text-primary h-4 w-4' />
              <Badge variant='secondary' className='text-xs'>
                {t(FIELD_TYPE_LABEL_KEYS[field.type])}
              </Badge>
            </div>
            <Button
              type='button'
              variant='ghost'
              size='icon'
              className='text-destructive hover:text-destructive h-8 w-8'
              onClick={() => onRemove(field.tempId)}
            >
              <Trash2 className='h-4 w-4' />
            </Button>
          </div>

          <div>
            <label className='mb-1.5 block text-sm font-medium'>
              {t('fieldLabel')}
            </label>
            <Input
              value={editLabel}
              onChange={(e) => {
                setEditLabel(e.target.value);
                if (labelError) setLabelError('');
              }}
              placeholder={t('fieldLabelPlaceholder')}
              autoFocus
            />
            {labelError && (
              <p className='mt-1 text-sm text-red-500'>{labelError}</p>
            )}
          </div>

          {field.type === 'number' && (
            <div>
              <label className='mb-1.5 block text-sm font-medium'>
                {t('defaultValueNumber')}
              </label>
              <Input
                type='number'
                value={editDefaultValue}
                onChange={(e) => setEditDefaultValue(e.target.value)}
                placeholder='0'
              />
            </div>
          )}

          {field.type === 'selection_list' && (
            <div>
              <div className='mb-3 flex items-center gap-2'>
                <Checkbox
                  id={`multi-select-${field.tempId}`}
                  checked={editMultiSelect}
                  onCheckedChange={(checked) =>
                    setEditMultiSelect(checked === true)
                  }
                />
                <label
                  htmlFor={`multi-select-${field.tempId}`}
                  className='text-sm font-medium'
                >
                  {t('allowMultiSelect')}
                </label>
              </div>
              <label className='mb-1.5 block text-sm font-medium'>
                {t('fieldOptions')}
              </label>
              <div className='space-y-2'>
                {editOptions.map((option, index) => (
                  <div key={index} className='flex items-center gap-2'>
                    <input
                      type='radio'
                      name={`default-${field.tempId}`}
                      checked={
                        option.trim() !== '' && option === editDefaultValue
                      }
                      onChange={() => setEditDefaultValue(option)}
                      title={t('setAsDefault')}
                      className='accent-primary h-4 w-4 shrink-0 cursor-pointer'
                    />
                    <Input
                      value={option}
                      onChange={(e) =>
                        handleOptionChange(index, e.target.value)
                      }
                      placeholder={t('optionPlaceholder', {
                        index: index + 1
                      })}
                    />
                    {editOptions.length > 2 && (
                      <Button
                        type='button'
                        variant='ghost'
                        size='icon'
                        className='h-8 w-8 shrink-0'
                        onClick={() => handleRemoveOption(index)}
                      >
                        <X className='h-4 w-4' />
                      </Button>
                    )}
                  </div>
                ))}
                <p className='text-muted-foreground text-xs'>
                  {t('setAsDefaultHint')}
                </p>
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  onClick={handleAddOption}
                >
                  <Plus className='me-1 h-4 w-4' />
                  {t('addOption')}
                </Button>
              </div>
            </div>
          )}

          <div className='flex items-center gap-2'>
            <Checkbox
              id={`required-${field.tempId}`}
              checked={editRequired}
              onCheckedChange={(checked) => setEditRequired(checked === true)}
            />
            <label
              htmlFor={`required-${field.tempId}`}
              className='text-sm font-medium'
            >
              {t('fieldRequired')}
            </label>
          </div>

          <div className='flex items-center gap-2'>
            <Button
              type='button'
              className='flex-1 bg-green-500 hover:bg-green-600'
              onClick={handleDone}
            >
              <Check className='me-1 h-4 w-4' />
              {t('done')}
            </Button>
            {field.label && (
              <Button
                type='button'
                variant='outline'
                onClick={() => {
                  setEditLabel(field.label);
                  setEditRequired(field.is_required);
                  setEditOptions(
                    field.options.length > 0 ? field.options : ['', '']
                  );
                  setEditDefaultValue(field.default_value || '');
                  setEditMultiSelect(field.is_multi_select ?? false);
                  setLabelError('');
                  setIsEditing(false);
                }}
              >
                {t('cancel')}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'border-border flex items-center gap-3 rounded-lg border p-3 transition-shadow',
        isDragging && 'opacity-50 shadow-lg'
      )}
    >
      {/* Drag handle */}
      <button
        type='button'
        className='text-muted-foreground/50 hover:text-muted-foreground cursor-grab touch-none active:cursor-grabbing'
        {...attributes}
        {...listeners}
      >
        <GripVertical className='h-4 w-4' />
      </button>

      {/* Field info */}
      <div className='flex min-w-0 flex-1 flex-wrap items-center gap-2'>
        <Icon className='text-muted-foreground h-4 w-4 shrink-0' />
        <span className='truncate font-medium'>
          {field.label || t(FIELD_TYPE_LABEL_KEYS[field.type])}
        </span>
        <Badge variant='secondary' className='text-xs'>
          {t(FIELD_TYPE_LABEL_KEYS[field.type])}
        </Badge>
        {field.is_required && (
          <Badge
            variant='outline'
            className='border-red-200 text-xs text-red-600 dark:border-red-800 dark:text-red-400'
          >
            {t('fieldRequired')}
          </Badge>
        )}
        {field.type === 'selection_list' && field.is_multi_select && (
          <Badge variant='secondary' className='text-xs'>
            {t('allowMultiSelect')}
          </Badge>
        )}
        {(field.type === 'selection_list' || field.type === 'number') &&
          field.default_value && (
            <Badge variant='outline' className='text-xs'>
              {t('defaultOption')}: {field.default_value}
            </Badge>
          )}
      </div>

      {/* Actions */}
      <div className='flex shrink-0 items-center gap-0.5'>
        <Button
          type='button'
          variant='ghost'
          size='icon'
          className='h-8 w-8'
          onClick={() => setIsEditing(true)}
        >
          <Pencil className='h-4 w-4' />
        </Button>
        {onDuplicate && (
          <Button
            type='button'
            variant='ghost'
            size='icon'
            className='h-8 w-8'
            onClick={() => onDuplicate(field.tempId)}
          >
            <Copy className='h-4 w-4' />
          </Button>
        )}
        <Button
          type='button'
          variant='ghost'
          size='icon'
          className='text-destructive hover:text-destructive h-8 w-8'
          onClick={() => onRemove(field.tempId)}
        >
          <Trash2 className='h-4 w-4' />
        </Button>
      </div>
    </div>
  );
}
